/* eslint-disable @typescript-eslint/no-explicit-any */
import { handlePasscodeErrorsInModal } from 'utils/passcodeUtils';
import AppConstants from 'constants/AppConstants';

import { CancellationType } from 'state/purchase';
import { RefundTypes } from 'constants/RefundTypes';
import { core, toEnumString } from '@tock/coretypes';
import protos from 'utils/protos';

const { PurchaseOrder, CancellationRequest, Refund, TransferTicketAdminRequest, PurchasePacing } =
  protos;
const {
  Discount,
  VisitEditInitiateRequest,
  VisitSendToKitchenRequest,
  ExecuteHoldRequest,
  RefundHoldRequest,
} = protos;

export interface RefundPurchaseRequest {
  purchaseDetail: core.PastPurchase;
  amountCents: number;
  emailPolicy: any;
  discountType: string;
  passcode?: string;
}

export function openCancel(
  purchaseDetail: core.PastPurchase,
  cancellationType: CancellationType,
  isFlexBusiness: boolean
): BaseTockAction {
  return {
    type: AppConstants.PURCHASE_CANCEL_OPEN,
    payload: { purchaseDetail, cancellationType, isFlexBusiness },
  };
}

export function cancelPurchase(
  purchaseDetail: core.PastPurchase,
  shouldRefund: any,
  amountCents: number,
  emailPolicy: any,
  makeAvailable: boolean,
  passcode?: string
): ApiAction {
  let refund: any = null;
  if (shouldRefund) {
    refund = {
      purchaseId: purchaseDetail.id,
      amount: amountCents,
      emailPolicy,
    };
  }
  const requestData = {
    purchaseId: purchaseDetail.id,
    refund,
    emailPolicy,
    makeAvailable,
  };

  return {
    TockApi: {
      postProto: '/admin/cancel',
      type: AppConstants.PURCHASE_CANCEL,
      request: CancellationRequest.fromObject(requestData),
      toast: 'Reservation canceled',
      requestScope: {
        passcode,
      },
      onError: {
        ...handlePasscodeErrorsInModal,
        defaultToOriginal: true,
      },
    },
  };
}

export function clearCancel(): BaseTockAction {
  return {
    type: AppConstants.PURCHASE_CANCEL_CLEAR,
  };
}

export function openComp(purchase: any): ApiAction {
  return {
    TockApi: {
      getProto: `/purchase/order/generate?purchaseId=${purchase.id}`,
      type: AppConstants.PURCHASE_COMP_OPEN,
      context: purchase,
    },
  };
}

export function getPurchaseOrderForHold(purchaseId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/purchase/order/hold/generate?purchaseId=${purchaseId}`,
      type: AppConstants.PURCHASE_CHARGE_BOOKING_POLICY_FEE_OPEN,
    },
  };
}

export function refundPurchase({
  purchaseDetail,
  amountCents,
  emailPolicy,
  discountType,
  passcode,
}: RefundPurchaseRequest): ApiAction {
  const refund = {
    purchaseId: purchaseDetail.id,
    amount: amountCents,
    emailPolicy,
    discountType: toEnumString(Discount.DiscountType, discountType),
  };
  return {
    TockApi: {
      postProto: '/admin/refund',
      type: AppConstants.PURCHASE_REFUND,
      request: Refund.fromObject(refund),
      toast: 'Refund applied to the purchase',
      requestScope: {
        passcode,
      },
      onError: {
        ...handlePasscodeErrorsInModal,
        defaultToOriginal: true,
      },
    },
  };
}

export function updateRefund(refundRequest: {
  refund: { amountCents: number; refundType: string | RefundTypes | null };
}): BaseTockAction {
  return {
    type: AppConstants.PURCHASE_REFUND_UPDATE,
    payload: refundRequest,
  };
}

export function updatePriceForRefund(newPurchaseOrder: core.PurchaseOrder): ApiAction {
  return {
    TockApi: {
      postProto: '/ticket/price',
      type: AppConstants.PURCHASE_REFUND_UPDATE_PURCHASE_ORDER,
      request: PurchaseOrder.fromObject({
        ...newPurchaseOrder,
        adminOverride: true,
      }),
    },
  };
}

export function clearRefund(): BaseTockAction {
  return {
    type: AppConstants.PURCHASE_REFUND_CLEAR,
  };
}

export function openTransfer(purchaseDetail: core.PastPurchase): BaseTockAction {
  return {
    type: AppConstants.PURCHASE_TRANSFER_OPEN,
    payload: purchaseDetail,
  };
}

export function transferViaEmail(
  purchaseDetail: core.PastPurchase,
  patron: any,
  discountCents: number
): ApiAction {
  const transferRequest = {
    purchaseId: purchaseDetail.id,
    transferFrom: purchaseDetail.ownerPatron,
    transferTo: patron,
    discount: discountCents,
  };
  if (purchaseDetail.transferred) {
    transferRequest.discount = purchaseDetail.amountPaidCents;
  }
  return {
    TockApi: {
      postProto: '/admin/transfer',
      type: AppConstants.NULL,
      request: TransferTicketAdminRequest.fromObject(transferRequest),
    },
  };
}

export function transferNow(purchaseDetail: core.PastPurchase, patron: any): ApiAction {
  const transferRequest = {
    purchaseId: purchaseDetail.id,
    transferFrom: purchaseDetail.ownerPatron,
    transferTo: patron,
    type: 'TRANSFER_NOW',
  };

  return {
    TockApi: {
      postProto: '/admin/transfer',
      type: AppConstants.NULL,
      request: TransferTicketAdminRequest.fromObject(transferRequest),
    },
  };
}

export function cancelTransfer(purchaseDetail: core.PastPurchase): ApiAction {
  return {
    TockApi: {
      deleteProto: `/admin/transfer/cancel?purchaseId=${purchaseDetail.id}`,
      type: AppConstants.NULL,
    },
  };
}

export function changeDiner(purchaseDetail: core.PastPurchase, patron: any): ApiAction {
  const transferRequest = {
    purchaseId: purchaseDetail.id,
    transferFrom: purchaseDetail.ownerPatron,
    transferTo: patron,
    type: 'CHANGE_DINER',
  };

  return {
    TockApi: {
      postProto: '/admin/transfer',
      type: AppConstants.NULL,
      request: TransferTicketAdminRequest.fromObject(transferRequest),
    },
  };
}

export function clearTransfer(): BaseTockAction {
  return {
    type: AppConstants.PURCHASE_TRANSFER_CLEAR,
  };
}

export function initiateTableSideOrder(
  visitId: number,
  sendText: boolean,
  requestPayment: boolean,
  patronInfo: {
    phone: string;
    firstName?: string;
    lastName?: string;
    arrivalTime?: number;
  },
  inStoreChargeCents?: number
): ApiAction {
  const request = VisitEditInitiateRequest.fromObject({
    visitId,
    requestPayment,
    phone: patronInfo.phone,
    inStoreChargeCents,
    sendText,
    arrivalTime: patronInfo.arrivalTime,
    firstName: patronInfo.firstName,
    lastName: patronInfo.lastName,
  });
  return {
    TockApi: {
      postProto: `/visit/edit/initiate`,
      request,
      type: AppConstants.NULL,
      toast: sendText
        ? requestPayment
          ? `Payment link was sent to ${patronInfo.phone}`
          : `Tableside ordering link was sent to ${patronInfo.phone}`
        : undefined,
    },
  };
}

export function initiateTableSideOrderEmail(
  visitId: number,
  requestPayment: boolean,
  patronInfo: {
    email: string;
    firstName?: string;
    lastName?: string;
    arrivalTime?: number;
  },
  inStoreChargeCents?: number
) {
  const request = VisitEditInitiateRequest.fromObject({
    visitId,
    requestPayment,
    email: patronInfo.email,
    inStoreChargeCents,
    arrivalTime: patronInfo.arrivalTime,
    firstName: patronInfo.firstName,
    lastName: patronInfo.lastName,
  });

  return {
    TockApi: {
      postProto: `/visit/edit/initiate/email`,
      request,
      type: AppConstants.NULL,
      toast: requestPayment
        ? `Payment link was sent to ${patronInfo.email}`
        : `Tableside ordering link was sent to ${patronInfo.email}`,
    },
  };
}

export function sendToKitchen(visitId: number): ApiAction {
  const request = VisitSendToKitchenRequest.fromObject({
    visitId,
  });
  return {
    TockApi: {
      postProto: `/visit/edit/send-to-kitchen`,
      request,
      type: AppConstants.NULL,
    },
  };
}

export function chargeBookingFee(purchaseId: number, passcode?: string): ApiAction {
  return {
    TockApi: {
      postProto: '/execute-hold',
      type: AppConstants.PURCHASE_CHARGE_BOOKING_POLICY_FEE,
      request: ExecuteHoldRequest.fromObject({ purchaseId }),
      requestScope: {
        passcode,
      },
    },
  };
}

export function refundBookingFee(purchaseId: number, passcode?: string): ApiAction {
  return {
    TockApi: {
      postProto: '/refund-hold',
      type: AppConstants.PURCHASE_REFUND_BOOKING_POLICY_FEE,
      request: RefundHoldRequest.fromObject({ purchaseId }),
      requestScope: {
        passcode,
      },
    },
  };
}

export function setExcludeFromPacing(purchaseId: number, val: boolean) {
  return {
    TockApi: {
      putProto: `/admin/purchase/${purchaseId}/exclude-from-pacing`,
      type: AppConstants.NULL,
      request: PurchasePacing.fromObject({ excludeFromPacing: val }),
    },
  };
}
